import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Box, Button, IconButton, Drawer,
  List, ListItem, ListItemText, Container, Divider, Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import ConstructionIcon from '@mui/icons-material/Construction';
import TranslateIcon from '@mui/icons-material/Translate';
import { useLang } from '../context/LanguageContext';

export default function Navbar() {
  const { t, toggle } = useLang();
  const n = t.nav;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setDrawerOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Top info bar — desktop only */}
      <Box sx={{ background: '#C8102E', py: 0.6, display: { xs: 'none', md: 'block' } }}>
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" sx={{ color: '#fff', letterSpacing: '0.05em' }}>
              {n.topBar}
            </Typography>
            <Box display="flex" alignItems="center" gap={3}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <PhoneIcon sx={{ color: '#fff', fontSize: 13 }} />
                <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, letterSpacing: '0.04em' }}>
                  {n.phone}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: '#fff', letterSpacing: '0.04em' }}>
                {n.email}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <AppBar
        position="sticky"
        elevation={scrolled ? 4 : 0}
        sx={{
          background: scrolled ? 'rgba(10,10,10,0.97)' : '#111111',
          backdropFilter: 'blur(8px)',
          borderBottom: scrolled ? 'none' : '1px solid rgba(255,255,255,0.06)',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="xl" disableGutters sx={{ px: { xs: 2, sm: 3 } }}>
          <Toolbar disableGutters sx={{ py: { xs: 0.5, md: 1 }, minHeight: { xs: 56, md: 64 } }}>

            {/* Logo */}
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              sx={{ cursor: 'pointer', flexGrow: { xs: 1, md: 0 }, mr: { md: 5 }, userSelect: 'none' }}
            >
              <Box sx={{ background: '#C8102E', p: 0.7, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <ConstructionIcon sx={{ color: '#fff', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{
                  fontFamily: '"Oswald", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '0.88rem', sm: '1rem' },
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#FFFFFF',
                  lineHeight: 1.1,
                }}>
                  Reliable
                </Typography>
                <Typography sx={{
                  fontFamily: '"Oswald", sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: '0.55rem', sm: '0.63rem' },
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: '#999',
                  lineHeight: 1.2,
                }}>
                  Maintenance & Construction Corp
                </Typography>
              </Box>
            </Box>

            {/* Desktop nav */}
            <Box display={{ xs: 'none', md: 'flex' }} alignItems="center" gap={0.5} sx={{ flexGrow: 1 }}>
              {n.links.map((label, i) => (
                <Button
                  key={label}
                  onClick={() => handleNavClick(n.hrefs[i])}
                  sx={{
                    color: '#CCCCCC',
                    fontFamily: '"Oswald", sans-serif',
                    fontWeight: 400,
                    fontSize: '0.82rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    px: 1.5,
                    py: 1,
                    borderRadius: 0,
                    '&:hover': { color: '#FFFFFF', background: 'rgba(200,16,46,0.12)' },
                  }}
                >
                  {label}
                </Button>
              ))}
            </Box>

            {/* Desktop: lang toggle + CTA */}
            <Box display={{ xs: 'none', md: 'flex' }} alignItems="center" gap={1.5}>
              <Button
                onClick={toggle}
                startIcon={<TranslateIcon sx={{ fontSize: '0.9rem !important' }} />}
                sx={{
                  color: '#AAAAAA',
                  fontFamily: '"Oswald", sans-serif',
                  fontSize: '0.78rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  borderRadius: 0,
                  px: 1.5,
                  py: 0.8,
                  border: '1px solid rgba(255,255,255,0.12)',
                  minWidth: 'auto',
                  '&:hover': { color: '#fff', borderColor: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.05)' },
                }}
              >
                {n.langToggle}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleNavClick('#contact')}
                sx={{ whiteSpace: 'nowrap', fontSize: '0.82rem', px: 3 }}
              >
                {n.cta}
              </Button>
            </Box>

            {/* Mobile: lang toggle + hamburger */}
            <Box display={{ xs: 'flex', md: 'none' }} alignItems="center" gap={0.5}>
              <IconButton
                onClick={toggle}
                sx={{
                  color: '#AAAAAA',
                  p: 0.6,
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 0,
                  '&:hover': { color: '#fff' },
                }}
              >
                <Typography sx={{ fontFamily: '"Oswald", sans-serif', fontSize: '0.68rem', letterSpacing: '0.1em', lineHeight: 1, px: 0.3 }}>
                  {n.langToggle}
                </Typography>
              </IconButton>
              <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: '#fff' }}>
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 280, background: '#111111' } }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontFamily: '"Oswald", sans-serif', color: '#fff', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {n.menu}
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <List disablePadding>
          {n.links.map((label, i) => (
            <ListItem
              key={label}
              onClick={() => handleNavClick(n.hrefs[i])}
              sx={{ cursor: 'pointer', py: 1.5, '&:hover': { background: 'rgba(200,16,46,0.15)' } }}
            >
              <ListItemText
                primary={label}
                primaryTypographyProps={{
                  sx: {
                    fontFamily: '"Oswald", sans-serif',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: '#BBBBBB',
                    fontWeight: 400,
                    fontSize: '0.95rem',
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
        <Box sx={{ p: 3, mt: 'auto' }}>
          <Button fullWidth variant="contained" color="primary" onClick={() => handleNavClick('#contact')} sx={{ mb: 2 }}>
            {n.cta}
          </Button>
          <Box display="flex" alignItems="center" gap={1} justifyContent="center">
            <PhoneIcon sx={{ color: '#C8102E', fontSize: 15 }} />
            <Typography variant="body2" sx={{ color: '#BBBBBB' }}>{n.phone}</Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
